import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    ClickAwayListener,
    InputAdornment,
    OutlinedInput,
    Paper,
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Typography,
    CircularProgress
} from '@material-ui/core';
import { IconSearch } from '@tabler/icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import API from '../../../../api/config/api';
import useDebounceInput from '../../../../hooks/useDebounceInput';

// Deliberately bypasses the shared getProjects/getUsers react-query hooks - those always
// fetch on mount with no "enabled" gate, which is fine for full list pages but wrong for a
// header widget that should stay quiet until the user actually types something.
const GlobalSearch = () => {
    const history = useHistory();
    const [value, setValue] = useState('');
    const debouncedValue = useDebounceInput(value, 350);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const term = debouncedValue.trim();
        if (term.length < 2) {
            setProjects([]);
            setUsers([]);
            setOpen(false);
            return;
        }

        const requestId = ++requestIdRef.current;
        setLoading(true);
        const api = new API();

        Promise.all([
            api.instance.get(`/projects?page=1&limit=5&search=${encodeURIComponent(term)}`),
            api.instance.get(`/users/list?page=1&limit=5&search=${encodeURIComponent(term)}`)
        ])
            .then(([projectsRes, usersRes]) => {
                if (requestId !== requestIdRef.current) return; // a newer search superseded this one
                setProjects(projectsRes.data.data.results || []);
                setUsers(usersRes.data.data.results || []);
                setOpen(true);
            })
            .catch(() => {
                if (requestId !== requestIdRef.current) return;
                setProjects([]);
                setUsers([]);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setLoading(false);
            });
    }, [debouncedValue]);

    const handleSelectProject = (id) => {
        setOpen(false);
        setValue('');
        history.push(`/project-view/${id}`);
    };

    const handleSelectUser = (id) => {
        setOpen(false);
        setValue('');
        history.push(`/user-edit/${id}`);
    };

    const hasResults = projects.length > 0 || users.length > 0;

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ position: 'relative', width: { xs: '100%', md: 320 }, ml: { md: 2 } }}>
                <OutlinedInput
                    fullWidth
                    size="small"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={() => hasResults && setOpen(true)}
                    placeholder="Search projects or users..."
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch size={18} stroke={1.5} />
                        </InputAdornment>
                    }
                    endAdornment={loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                />
                {open && (
                    <Paper
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            mt: 0.5,
                            zIndex: 1300,
                            maxHeight: 400,
                            overflow: 'auto'
                        }}
                    >
                        {!hasResults && (
                            <Typography sx={{ p: 2 }} color="textSecondary" variant="body2">
                                No results for "{debouncedValue}"
                            </Typography>
                        )}
                        {projects.length > 0 && (
                            <List dense subheader={<ListSubheader>Projects</ListSubheader>}>
                                {projects.map((project) => (
                                    <ListItemButton key={project.id} onClick={() => handleSelectProject(project.id)}>
                                        <ListItemText primary={project.title} secondary={project.address} />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                        {users.length > 0 && (
                            <List dense subheader={<ListSubheader>Users</ListSubheader>}>
                                {users.map((user) => (
                                    <ListItemButton key={user.id} onClick={() => handleSelectUser(user.id)}>
                                        <ListItemText
                                            primary={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                                            secondary={user.email}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </Paper>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default GlobalSearch;
